import { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import styled from 'styled-components';
import CompilerSelectControlledComponent from 'components/v2/epd-compiler-inputs/CompilerSelectControlledComponent';
import WizardTextInputControlledComponent from 'components/v2/epd-wizard-inputs/WizardTextInputControlled';
import { HelpBox } from 'components/v2/help-boxes';
import { toaster } from 'components/v2/toast';
import { useVerifierMarketplaceCountriesLookupWithGlobal } from 'hooks/lookups';
import CompanyService from 'services/CompanyService';
import { DeveloperCompanyModel } from 'services/EpdClient';
import { Option } from 'types/types';
import { AddPractitionerToCompanyModel } from 'types/dynamic-forms/companies/AddPractitionerToCompanyModel';
import LcaCompaniesList from 'components/v2/compiler/panels/lca-support/lca-practitioner-selector/LcaCompaniesList';
import InviteExternalPractitionerTextInput from 'components/v2/compiler/panels/lca-support/lca-practitioner-selector/InviteExternalPractitionerTextInput';
import { COLLABORATION_REQUEST_SENT_TEXT } from 'components/v2/compiler/constants';
import { postRequestLcaPractitionerCollaboration } from 'services/api/dynamicFormsCompaniesApi';

type TProps = {
  onHide(): void;
  isOpened: boolean;
  companyId: string;
  selectedPractitioners?: Option[] | undefined;
  companyPractitioners?: Option[] | undefined;
  onAddPractitioner(model: AddPractitionerToCompanyModel): Promise<void>;
};

const LcaPractitionerSelectInputRendererDialog = ({ onHide, isOpened, companyId, selectedPractitioners, companyPractitioners, onAddPractitioner }: TProps) => {
  const [selectedCountry, setSelectedCountry] = useState<Option | undefined>();
  const [lcaPractitionerCompanies, setLcaPractitionerCompanies] = useState<DeveloperCompanyModel[]>([]);
  const [filteredLcaPractitionerCompanies, setFilteredLcaPractitionerCompanies] = useState<DeveloperCompanyModel[]>([]);
  const [selectedLcaPractitionerCompany, setSelectedLcaPractitionerCompany] = useState<DeveloperCompanyModel | undefined>();

  const [areButtonsDisabled, setAreButtonsDisabled] = useState<boolean>(false);

  const countries = useVerifierMarketplaceCountriesLookupWithGlobal().data || [];

  useEffect(() => {
    const fetchLcaPractitionerCompanies = async () => {
      const res = await CompanyService.getDevelopers(undefined, selectedCountry?.value);
      const companies = [...res].sort((a, b) => ((a.name ?? '') > (b.name ?? '') ? 1 : -1));
      setLcaPractitionerCompanies(companies);
      setFilteredLcaPractitionerCompanies(companies);
    };

    fetchLcaPractitionerCompanies();
  }, [selectedCountry]);

  const onChangeLcaPractitionerCountry = async (_: string, val: any) => {
    if (!countries.length) return;
    setSelectedCountry(countries.find((c) => c.value === val));
  };

  const handleAddLcaPractitioner = async (membershipId: string) => {
    setAreButtonsDisabled(true);

    try {

      const connectPractitionerModel: AddPractitionerToCompanyModel = {
        companyId: companyId,
        membershipId: membershipId,
      };
      await onAddPractitioner(connectPractitionerModel);
      toaster({
        severity: 'success',
        summary: 'The LCA Practitioner was added to the EPD!',
        details: 'Now the EPD Practitioner has an access to the EPD.',
      });
      onHide();
    } catch {
    } finally {
      setAreButtonsDisabled(false);
    }
  };

  const handleRequestCollaboration = async (targetCompanyId: string) => {
    setAreButtonsDisabled(true);

    try {
      await postRequestLcaPractitionerCollaboration(companyId, targetCompanyId);
      setFilteredLcaPractitionerCompanies((prev) =>
        prev.map((c) => (c.id === targetCompanyId ? { ...c, successOperationText: COLLABORATION_REQUEST_SENT_TEXT } : c))
      );
      toaster({
        severity: 'success',
        summary: 'Your collaboration request has been sent.',
        details: 'Please wait for a confirmation response.',
      });
    } catch {
      setFilteredLcaPractitionerCompanies((prev) =>
        prev.map((c) => (c.id === targetCompanyId ? { ...c, errorText: 'Something went wrong.' } : c))
      );
    } finally {
      setAreButtonsDisabled(false);
    }
  };

  const handleSearchLcaPractitionerCompanies = (event: any) => {
    const searchTerm = event.target.value.toLowerCase();
    if (!searchTerm) {
      setFilteredLcaPractitionerCompanies(lcaPractitionerCompanies);
      return;
    }
    setFilteredLcaPractitionerCompanies((_) =>
      lcaPractitionerCompanies.filter(
        (item) =>
          item.name?.toLowerCase().includes(searchTerm) ||
          item.members?.some((m) => m.email?.toLowerCase().includes(searchTerm))
      )
    );
  };

  return (
    <StyledDialog
      resizable={false}
      header="LCA Practitioner"
      footer={
        <DialogFooter>
          <HelpBox>
            Can't find your LCA Practitioner in the list? Enter their email below to send an invitation to the EPD Portal.
            Once they register, you will be able to share access to your EPD.
          </HelpBox>
          <InviteExternalPractitionerWrapper>
            <InviteExternalPractitionerTextInput companyId={companyId} />
          </InviteExternalPractitionerWrapper>
        </DialogFooter>
      }
      visible={isOpened}
      contentStyle={!!filteredLcaPractitionerCompanies.length ? {} : { overflowY: 'initial' }}
      onHide={() => {
        setSelectedCountry(undefined);
        onHide();
      }}
    >
      <DialogContentContainer>
        <LcaDialogHelpBox>
          <div>
            You can add LCA Practitioners to your project in two ways:
            <ul>
              <li>
                If you have already agreed to work with a practitioner from the list below, click <b>"Add practitioner"</b>
                &nbsp; to assign them directly to your EPD.
              </li>
              <li>
                If you need to discuss collaboration first, select one or more practitioners and click{' '}
                <b>"Request collaboration"</b>&nbsp; to notify them of your interest in collaborating on LCA/EPD development.
                Once confirmed, use <b>"Add practitioner"</b>&nbsp; as described above.
              </li>
            </ul>
          </div>
        </LcaDialogHelpBox>
        <DialogContentRow>
          <CompilerSelectControlledComponent
            label="Country"
            name="lcaPractitionerCountry"
            options={countries}
            value={selectedCountry}
            onChanged={onChangeLcaPractitionerCountry}
            placeholder="Select"
            isClearable={true}
          />
        </DialogContentRow>
        <DialogContentRow>
          <WizardTextInputControlledComponent
            label="Organisation name or e-mail address"
            tooltipPosition={'right'}
            value={undefined}
            placeholder="Type here to search"
            onChange={handleSearchLcaPractitionerCompanies}
            onChanged={() => {}}
            name="nameOrEmail"
            maxLength={150}
          />
        </DialogContentRow>
        <LcaCompaniesList
          selectedPractitioners={selectedPractitioners}
          companyPractitioners={companyPractitioners}
          companies={filteredLcaPractitionerCompanies}
          selectedCompany={selectedLcaPractitionerCompany}
          onSelectCompany={setSelectedLcaPractitionerCompany}
          onRequestCollaboration={handleRequestCollaboration}
          onAddPractitioner={handleAddLcaPractitioner}
          buttonsAreDisabled={areButtonsDisabled}
        />
      </DialogContentContainer>
    </StyledDialog>
  );
};

const StyledDialog = styled(Dialog)`
  width: 35rem;

  && .p-dialog-footer {
    padding-bottom: 30px;
    height: auto;
  }
`;

const DialogContentContainer = styled.div`
  padding: 0.25rem 2rem 2rem 2rem;
  scrollbar-gutter: stable;
`;

const DialogContentRow = styled.div`
  margin-top: 25px;
  margin-bottom: 8px;
`;

const DialogFooter = styled.div`
  padding-left: 1rem;
  padding-right: 1rem;
`;

const InviteExternalPractitionerWrapper = styled.div`
  margin-top: 8px;
`;

const LcaDialogHelpBox = styled(HelpBox)`
  ul {
    margin: 0;
    padding-left: 30px;
  }
`;

export default LcaPractitionerSelectInputRendererDialog;
